import React from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { playClickSound } from '@/utils/soundUtils';
import { useTranslation } from 'react-i18next';
import { isForgotPasswordLogin } from '@/features/auth/constants/authRoutes';
import { typeRole, fsClass } from '@/styles/typography';

interface AuthHeaderProps {
  className?: string;
  children?: React.ReactNode;
  title?: string;
  subtitle?: string;
}

/**
 * Header component for authentication pages (User Login, Activate, Forgot Password, Reset Password)
 * Based on Figma design with back button, logo, title and subtitle
 */
const AuthHeader: React.FC<AuthHeaderProps> = ({
  className = '',
  children,
  title: propTitle,
  subtitle: propSubtitle
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isForgotLogin = location.pathname === '/login' && isForgotPasswordLogin(searchParams);

  // Determine title and subtitle based on route
  const getTitle = () => {
    if (propTitle) return propTitle;
    if (isForgotLogin) return t('authHeader.forgotPassword');
    if (location.pathname === '/login') return t('authHeader.userLogin');
    if (location.pathname === '/activate') return t('authHeader.accountActivation');
    if (location.pathname === '/forgot-password') return t('authHeader.forgotPassword');
    if (location.pathname === '/reset-password') return t('authHeader.resetPassword');
    return t('authHeader.userLogin');
  };

  const getSubtitle = () => {
    if (propSubtitle) return propSubtitle;
    if (isForgotLogin) return t('authHeader.forgotSubtitle');
    if (location.pathname === '/login') return t('authHeader.verifyIdentitySubtitle');
    if (location.pathname === '/activate') return t('authHeader.activationSubtitle');
    if (location.pathname === '/forgot-password') return t('authHeader.forgotSubtitle');
    if (location.pathname === '/reset-password') return t('authHeader.resetSubtitle');
    return t('authHeader.verifyIdentitySubtitle');
  };

  const title = getTitle();
  const subtitle = getSubtitle();

  const handleBackClick = () => {
    playClickSound();
    navigate('/');
  };

  if (children) {
    return <header className={`w-full ${className}`}>{children}</header>;
  }

  const isLoginPage = location.pathname === '/login' || location.pathname === '/forgot-password';

  return (
    <header
      className={`
        relative h-full min-h-0 flex items-center w-full
        px-3 sm:px-4 md:px-5 lg:px-8 py-1.5 sm:py-2
        border-b border-border-ice bg-space-top/85 backdrop-blur-md
        ${className}
      `}
    >
      {isLoginPage ? (
        <div className="relative w-full flex items-center justify-center">
          <button
            onClick={handleBackClick}
            className="absolute left-0 top-1/2 -translate-y-1/2 flex items-center gap-1 sm:gap-1.5 min-h-[40px] text-caption-muted hover:text-ice transition-colors py-1"
          >
            <ArrowLeft className="w-4 h-4 flex-shrink-0" />
            <span className={`hidden sm:inline-block font-ui ${fsClass.base} font-semibold uppercase tracking-[0.2em]`}>
              {t('authHeader.backToHome')}
            </span>
          </button>

          <div className="hidden md:flex flex-col items-center justify-center gap-0.5 pointer-events-none">
            <h1 className={`text-ink ${typeRole.header1} font-extrabold italic tracking-wide text-center`}>
              {title}
            </h1>
            <div className="h-0.5 w-10 sm:w-12 lg:w-14 cosmos-beam rounded-full" />
            <p className={`font-ui text-caption-muted ${fsClass.base} font-semibold tracking-[0.14em] uppercase text-center max-w-[200px] sm:max-w-none truncate`}>
              {subtitle}
            </p>
          </div>
        </div>
      ) : (
        <div className="w-full flex flex-row items-center justify-between gap-2">
          <button
            onClick={handleBackClick}
            className="flex items-center gap-1 sm:gap-1.5 min-h-[40px] text-caption-muted hover:text-ice transition-colors py-1 flex-shrink-0"
          >
            <ArrowLeft className="w-4 h-4 flex-shrink-0" />
            <span className={`font-ui ${typeRole.caption} font-semibold uppercase tracking-[0.2em]`}>
              {t('authHeader.backToHome')}
            </span>
          </button>

          <div className="hidden md:flex flex-1 flex-col items-center justify-center gap-0.5 min-w-0">
            <h1 className={`text-ink ${typeRole.header1} font-extrabold italic tracking-wide text-center truncate w-full`}>
              {title}
            </h1>
            <div className="h-0.5 w-10 sm:w-12 lg:w-14 cosmos-beam rounded-full" />
            <p className={`font-ui text-caption-muted ${fsClass.base} font-semibold tracking-[0.14em] uppercase text-center truncate w-full`}>
              {subtitle}
            </p>
          </div>

          <div className="w-4 flex-shrink-0 sm:w-20" aria-hidden />
        </div>
      )}
    </header>
  );
};

export default AuthHeader;
